/**
 * AdvancedTab
 * Houses low-level details: raw config JSON, identifiers/endpoints, sync
 * metadata, and a clearly-separated danger zone.
 */

import React from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Code as CodeIcon,
  DeleteForever as DeleteIcon,
  Info as InfoIcon,
  Link as LinkIcon,
} from '@mui/icons-material';

import type { AgentDetail as AgentDetailType } from '../../../types';
import { SectionCard, CopyButton, KeyValue } from './primitives';

interface AdvancedTabProps {
  agent: AgentDetailType;
  onDelete: () => void;
  canDelete?: boolean;
}

export const AdvancedTab: React.FC<AdvancedTabProps> = ({
  agent,
  onDelete,
  canDelete = true,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const configJson = JSON.stringify(agent.config || {}, null, 2);
  const apiPath = `/ai/expert/api/agent-system/agents/${agent.id}/`;

  return (
    <Stack spacing={{ xs: 2, sm: 2.5 }}>
      <SectionCard
        icon={<InfoIcon fontSize="small" />}
        title="Identifiers"
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <KeyValue label="Agent ID" value={agent.id} copyValue={String(agent.id)} mono />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <KeyValue
              label="LLM model"
              value={agent.model?.model || agent.model?.name || '—'}
              copyValue={agent.model?.model || ''}
              mono
            />
          </Grid>
          {agent.embedding_model ? (
            <Grid size={{ xs: 12, sm: 6 }}>
              <KeyValue
                label="Embedding model"
                value={agent.embedding_model.model || agent.embedding_model.name}
                copyValue={agent.embedding_model.model || ''}
                mono
              />
            </Grid>
          ) : null}
          {agent.template_info?.template_id ? (
            <Grid size={{ xs: 12, sm: 6 }}>
              <KeyValue
                label="Source template id"
                value={agent.template_info.template_id}
                copyValue={String(agent.template_info.template_id)}
                mono
              />
            </Grid>
          ) : null}
        </Grid>
      </SectionCard>

      <SectionCard icon={<LinkIcon fontSize="small" />} title="API endpoint">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 1,
            bgcolor: alpha(theme.palette.background.default, 0.6),
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontFamily: 'monospace', flex: 1, wordBreak: 'break-all' }}
          >
            GET {apiPath}
          </Typography>
          <CopyButton value={apiPath} label="Endpoint" />
        </Box>
      </SectionCard>

      <SectionCard icon={<CodeIcon fontSize="small" />} title="Raw config" actions={<CopyButton value={configJson} label="Config JSON" />}>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 2,
            maxHeight: 360,
            overflow: 'auto',
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            bgcolor: isDark ? alpha('#000', 0.3) : alpha('#000', 0.03),
            fontSize: '0.78rem',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          }}
        >
          {configJson}
        </Box>
      </SectionCard>

      <SectionCard
        icon={<DeleteIcon fontSize="small" />}
        title="Danger zone"
        description="Destructive actions are permanent and cannot be undone."
      >
        <Alert severity="warning" sx={{ mb: 2 }}>
          Deleting an agent also removes its tool assignments, memories, and execution
          history.
        </Alert>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={onDelete}
          disabled={!canDelete}
        >
          Delete this agent
        </Button>
      </SectionCard>
    </Stack>
  );
};

export default AdvancedTab;
